import { queryOptions } from "@tanstack/react-query";
import { HTTPError } from "ky";
import type {
  ExtractedEntity,
  FileScanAction,
  FileScanStatusResponse,
  FileScanSubmitRequest,
  FileScanType,
} from "@/lib/http/pii-file-scan";
import { api } from "@/lib/http";

export const FALLBACK_ALLOWED_FILE_TYPES: Record<string, string> = {
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx",
  "image/png": "image",
  "image/jpeg": "image",
  "image/tiff": "image",
  "image/bmp": "image",
  "image/webp": "image",
};

const MIME_EXTENSIONS: Record<string, Array<string>> = {
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [
    ".pptx",
  ],
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/tiff": [".tif", ".tiff"],
  "image/bmp": [".bmp"],
  "image/webp": [".webp"],
};

export function allowedFileTypesQueryOptions() {
  return queryOptions({
    queryKey: ["pii-file-scan", "allowed-file-types"],
    queryFn: api.piiFileScan.allowedFileTypes,
    staleTime: Infinity,
  });
}

export function mimeToExtensions(mime: string, category: string) {
  if (mime in MIME_EXTENSIONS) return MIME_EXTENSIONS[mime];
  const [, subtype = category] = mime.split("/");
  return [`.${subtype.replace(/^x-/, "")}`];
}

export function acceptAttrFromAllowedTypes(types: Record<string, string>) {
  return [
    ...new Set(
      Object.entries(types).flatMap(([mime, category]) =>
        mimeToExtensions(mime, category),
      ),
    ),
  ].join(",");
}

export function isFileTypeAllowed(
  file: File,
  types: Record<string, string>,
): boolean {
  if (file.type && file.type in types) return true;
  const ext = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`;
  return Object.entries(types).some(([mime, category]) =>
    mimeToExtensions(mime, category).includes(ext),
  );
}

export type DocumentRedactResult =
  | {
      scan_type: "annotate";
      file_name: string;
      output_file_url: string;
    }
  | {
      scan_type: "extract";
      file_name: string;
      entities: Array<ExtractedEntity>;
      num_tokens: number | null;
    };

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function errorMessage(error: unknown): Promise<string> {
  if (error instanceof HTTPError) {
    try {
      const body: unknown = await error.response.json();
      if (body && typeof body === "object" && "detail" in body) {
        if (typeof body.detail === "string") return body.detail;
        if (body.detail != null) return JSON.stringify(body.detail);
      }
    } catch {
      // fall through to status text
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return "Something went wrong";
}

export type RunFileScanInput = {
  file: File;
  scan_type: FileScanType;
  entity_list: Array<string>;
  action?: FileScanAction;
  aggregate_entities?: boolean;
  detect_pid?: boolean;
  detect_signature?: boolean;
  detect_fingerprint?: boolean;
};

export async function runFileScan(
  input: RunFileScanInput,
): Promise<DocumentRedactResult> {
  const body: FileScanSubmitRequest = {
    file_name: input.file.name,
    scan_type: input.scan_type,
    entity_list: input.entity_list,
    detect_pid: input.detect_pid,
  };

  if (input.scan_type === "annotate") {
    body.action = input.action ?? "redact";
    body.detect_signature = input.detect_signature;
    body.detect_fingerprint = input.detect_fingerprint;
  } else {
    body.aggregate_entities = input.aggregate_entities;
  }

  try {
    const submitted = await api.piiFileScan.submit(body);
    if (!submitted.upload_url) {
      throw new Error("No upload URL returned");
    }

    await api.piiFileScan.uploadFile(submitted.upload_url, input.file);
    await api.piiFileScan.completeUpload(submitted.id);

    let status: FileScanStatusResponse;
    do {
      status = await api.piiFileScan.getStatus(submitted.id);
      if (status.status === "failed") {
        throw new Error(status.error_message || "Scan failed");
      }
      if (status.status === "cancelled") {
        throw new Error("Scan was cancelled");
      }
      if (status.status !== "done") {
        await sleep(1000);
      }
    } while (status.status !== "done");

    const fileName = status.file_name ?? input.file.name;
    if (status.scan_type === "annotate") {
      if (typeof status.result !== "string" || !status.result) {
        throw new Error("Scan finished without an output file");
      }
      return {
        scan_type: "annotate",
        file_name: fileName,
        output_file_url: status.result,
      };
    }

    const entities = Array.isArray(status.result) ? status.result : [];
    return {
      scan_type: "extract",
      file_name: fileName,
      entities,
      num_tokens: status.num_tokens,
    };
  } catch (error) {
    throw new Error(await errorMessage(error));
  }
}
